/**
 * Two-goal cart progress bar (free shipping + free gift) and the free-gift
 * cart sync (add/remove the configured gift line item as the cart subtotal
 * crosses the gift threshold).
 *
 * Rendered only when snippets/free-shipping-bar.liquid resolves the gift
 * tier as enabled + available; falls back to the theme's own single-goal
 * <free-shipping-bar> element otherwise, so this file has no effect when
 * the gift tier is off.
 *
 * Sync strategy: the theme's <cart-drawer>/<cart-page> elements emit their
 * own "update-nodes" event (a lightweight internal pub-sub, not a native
 * DOM CustomEvent) every time a cart mutation resolves, carrying a freshly
 * server-rendered copy of that section's HTML. The theme's own wrapper for
 * the single-goal bar only recognises the <free-shipping-bar> tag, so it
 * silently no-ops for our <cart-goals-bar> tag — this file registers its
 * own listener on the same event instead of polling/patching fetch, which
 * is what previously caused stale text after a plain quantity change and a
 * delayed gift add that only appeared after a full reload.
 */
(function () {
  'use strict';

  // The drawer body (script tag included) lives inside the section's
  // <template> and is re-mounted on every drawer open — guard against the
  // whole module re-running when that inserts a fresh copy of this script.
  if (window.__cartGoalsBarLoaded) return;
  window.__cartGoalsBarLoaded = true;

  var TAG = 'cart-goals-bar';
  var GIFT_PROPERTY = '_free_gift';

  // TEMPORARY diagnostic logging — remove once the gift add/remove issue is
  // confirmed fixed. Every relevant decision point logs here so we can see
  // exactly where the chain stops in the browser console.
  var DEBUG = true;
  function log() {
    if (!DEBUG) return;
    var args = ['[cart-goals-bar]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function formatDiff(currencyFormat, diffNumber) {
    var withComma =
      currencyFormat.indexOf('amount_with_comma_separator') !== -1 ||
      currencyFormat.indexOf('amount_with_space_separator') !== -1;
    var formatted = currencyFormat.replace(/\{\{.+?\}\}/g, String(diffNumber));
    return withComma ? formatted.replace('.', ',') : formatted;
  }

  function currencyRate() {
    return (window.Shopify && window.Shopify.currency && +window.Shopify.currency.rate) || 1;
  }

  // Merchant-editable messages are HTML-escaped in the Liquid data attribute
  // (they can contain apostrophes, e.g. "You've unlocked...") — decode back
  // to plain text before using them as a text node.
  var entityDecoder;
  function decodeEntities(str) {
    if (!str) return str;
    entityDecoder = entityDecoder || document.createElement('textarea');
    entityDecoder.innerHTML = str;
    return entityDecoder.value;
  }

  // ===================================================================
  // <cart-goals-bar> — renders message + fill + marker state.
  // ===================================================================
  function render(el) {
    var ds = el.dataset;
    var rate = currencyRate();
    var shippingThreshold = +ds.shippingThreshold * rate;
    var giftThreshold = +ds.giftThreshold * rate;
    var cartTotal = +ds.cartTotal;
    var currencyFormat = ds.currencyFormat || '';
    var noDecimals = currencyFormat.indexOf('no_decimals') !== -1;

    var messageNode = el.querySelector('[data-cart-goals-bar-message]');
    var fillNode = el.querySelector('[data-cart-goals-bar-fill]');
    var shippingMarker = el.querySelector('[data-cart-goals-bar-marker="shipping"]');
    var giftMarker = el.querySelector('[data-cart-goals-bar-marker="gift"]');

    var shippingUnlocked = cartTotal >= shippingThreshold;
    var giftUnlocked = giftThreshold > 0 && cartTotal >= giftThreshold;

    var text;
    if (!shippingUnlocked) {
      var shippingDiff = (shippingThreshold - cartTotal).toFixed(noDecimals ? 0 : 2);
      text = (ds.msgShippingProgress || '').replace('[value]', formatDiff(currencyFormat, shippingDiff));
    } else if (!giftUnlocked) {
      var giftDiff = (giftThreshold - cartTotal).toFixed(noDecimals ? 0 : 2);
      text = decodeEntities(ds.msgGiftProgress || '').replace('[value]', formatDiff(currencyFormat, giftDiff));
    } else {
      text = decodeEntities(ds.msgBothAchieved || '');
    }

    if (messageNode) messageNode.replaceChildren(text);

    var fillPercent = giftThreshold > 0 ? Math.max(0, Math.min((cartTotal / giftThreshold) * 100, 100)) : 0;
    if (fillNode) fillNode.style.width = fillPercent + '%';

    var shippingMarkerPercent =
      giftThreshold > 0 ? Math.max(0, Math.min((shippingThreshold / giftThreshold) * 100, 96)) : 0;
    if (shippingMarker) {
      shippingMarker.style.left = shippingMarkerPercent + '%';
      shippingMarker.setAttribute('data-achieved', shippingUnlocked ? 'true' : 'false');
    }
    if (giftMarker) {
      giftMarker.setAttribute('data-achieved', giftUnlocked ? 'true' : 'false');
    }
  }

  if (!customElements.get(TAG)) {
    customElements.define(
      TAG,
      class extends HTMLElement {
        connectedCallback() {
          render(this);
        }
      }
    );
  }

  // ===================================================================
  // Free-gift cart sync.
  // ===================================================================
  function routesUrl(key, fallback) {
    return (window.routes && window.routes[key]) || fallback;
  }

  function getHosts() {
    var hosts = [];
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.dataset.sectionId) hosts.push(drawer);
    else if (drawer) log('getHosts: <cart-drawer> found but no data-section-id, skipping');
    var page = document.querySelector('cart-page');
    if (page && page.dataset.sectionId) hosts.push(page);
    return hosts;
  }

  // Reads gift-relevant state straight out of a (possibly detached) parsed
  // section document — either the live `document` on initial load, or the
  // freshly server-rendered `node` handed to us by an "update-nodes" event.
  // Avoids a live cart.js fetch entirely: the bar's own data attributes and
  // the cart-item's `data-is-gift` flag already carry everything needed.
  function stateFromRoot(root) {
    var bar = root.querySelector(TAG);
    if (!bar) {
      log('stateFromRoot: no', TAG, 'element found in root', root);
      return null;
    }
    var ds = bar.dataset;
    var giftVariantId = parseInt(ds.giftVariantId, 10);
    if (!giftVariantId) {
      log('stateFromRoot: bar found but giftVariantId is falsy', ds.giftVariantId);
      return null;
    }
    var giftLineEl = root.querySelector('.cart-item[data-is-gift="true"]');
    var state = {
      cartTotal: +ds.cartTotal,
      giftThreshold: +ds.giftThreshold * currencyRate(),
      giftVariantId: giftVariantId,
      giftLine: giftLineEl
        ? { key: giftLineEl.dataset.key, quantity: +giftLineEl.dataset.quantity }
        : null
    };
    log('stateFromRoot:', state);
    return state;
  }

  var mutateInFlight = false;
  function runMutation(fn) {
    if (mutateInFlight) {
      log('runMutation: skipped, another mutation already in flight');
      return;
    }
    mutateInFlight = true;
    fn().finally(function () {
      mutateInFlight = false;
      log('runMutation: mutation settled, flag cleared');
    });
  }

  function reconcileState(state) {
    if (!state) return;
    var shouldHaveGift = state.giftThreshold > 0 && state.cartTotal >= state.giftThreshold;
    log('reconcileState: shouldHaveGift =', shouldHaveGift, 'giftLine =', state.giftLine);

    if (shouldHaveGift && !state.giftLine) {
      log('reconcileState: → adding gift, variant', state.giftVariantId);
      runMutation(function () {
        return addGift(state.giftVariantId);
      });
    } else if (!shouldHaveGift && state.giftLine) {
      log('reconcileState: → removing gift line', state.giftLine.key);
      runMutation(function () {
        return removeGiftLine(state.giftLine.key);
      });
    } else if (shouldHaveGift && state.giftLine && state.giftLine.quantity !== 1) {
      log('reconcileState: → fixing gift quantity', state.giftLine);
      runMutation(function () {
        return fixGiftQuantity(state.giftLine.key);
      });
    } else {
      log('reconcileState: no action needed');
    }
  }

  function mutateCart(url, extraBody) {
    var hosts = getHosts();
    var sectionIds = hosts
      .map(function (h) {
        return h.dataset.sectionId;
      })
      .join(',');

    var body = Object.assign({}, extraBody);
    if (sectionIds) {
      body.sections = sectionIds;
      body.sections_url = window.location.pathname;
    }

    log('mutateCart: POST', url, body);

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (data) {
          log('mutateCart: response', r.status, data);
          if (!r.ok) throw new Error('Cart mutate failed: ' + JSON.stringify(data));
          return data;
        });
      })
      .then(function (data) {
        if (data && data.sections) {
          hosts.forEach(function (host) {
            var html = data.sections[host.dataset.sectionId];
            if (html == null) return;
            applyFreshSection(host, html);
          });
        } else {
          log('mutateCart: response had no sections key');
        }
        return data;
      });
  }

  // Applies a freshly server-rendered section to a host the same way the
  // theme's own CartDrawer.updateItem does: extract the drawer body from the
  // section's <template data-sidebar-template> via the host's own
  // getTemplateContent(), then emit the internal "update-nodes" event so
  // every theme block (items list, footer, header, count) surgically
  // repaints itself from it. Wholesale child replacement doesn't work here:
  // the section HTML keeps the body inside an inert <template>, while an
  // open drawer has it mounted directly — swapping children would blank it.
  function applyFreshSection(host, html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    if (host.tagName.toLowerCase() === 'cart-drawer' && typeof host.getTemplateContent === 'function') {
      var fresh = host.getTemplateContent(doc);
      if (!fresh) {
        log('applyFreshSection: getTemplateContent returned nothing');
        return;
      }
      host.lastDrawerBody = fresh;
      if (host.isOpen) {
        log('applyFreshSection: drawer open → emitting update-nodes');
        host.emit('update-nodes', { node: fresh });
        if (typeof host.updateEmptyStatus === 'function') host.updateEmptyStatus();
      } else {
        // Closed drawer: its body is unmounted, so no block listeners are
        // bound — swap the detached element the theme will mount on next open.
        log('applyFreshSection: drawer closed → replacing detached element');
        host.element = fresh;
      }
    } else if (typeof host.emit === 'function') {
      log('applyFreshSection: emitting update-nodes on', host.tagName);
      host.emit('update-nodes', { node: doc });
    }
  }

  function addGift(variantId) {
    return mutateCart(routesUrl('cart_add_url', '/cart/add.js'), {
      items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }]
    }).catch(function (e) {
      console.warn('[cart-goals-bar] add gift failed (likely out of stock)', e);
    });
  }

  function removeGiftLine(key) {
    return mutateCart(routesUrl('cart_change_url', '/cart/change.js'), { id: key, quantity: 0 }).catch(
      function (e) {
        console.warn('[cart-goals-bar] remove gift failed', e);
      }
    );
  }

  function fixGiftQuantity(key) {
    return mutateCart(routesUrl('cart_change_url', '/cart/change.js'), { id: key, quantity: 1 }).catch(
      function () {}
    );
  }

  // Keep the bar element itself in sync (message/fill/markers) AND re-run
  // the gift reconcile, every time the theme's own cart-drawer/cart-page
  // component reports a fresh render — this is the same signal the theme
  // uses internally to update item lists, subtotals, headers, etc.
  function bindHost(host) {
    if (host.__cartGoalsBarBound) return;
    if (typeof host.on !== 'function') {
      log('bindHost: host has no .on() method, cannot bind', host.tagName, host);
      return;
    }
    host.__cartGoalsBarBound = true;
    log('bindHost: bound to', host.tagName, 'section id', host.dataset.sectionId);

    host.on('update-nodes', function (data) {
      log('update-nodes fired on', host.tagName);
      var node = data && data.node;
      if (!node) {
        log('update-nodes: no node in event data, skipping');
        return;
      }

      // Read gift state BEFORE touching the fragment, and CLONE the bar for
      // the swap instead of moving it — replaceWith(newBar) would rip the
      // bar out of `node`, so the stateFromRoot(node) call right after found
      // no bar and silently skipped the gift reconcile. That was the bug
      // that made the gift only appear after a full page refresh.
      var state = stateFromRoot(node);

      var newBar = node.querySelector(TAG);
      var oldBar = host.querySelector(TAG);
      if (newBar && oldBar) oldBar.replaceWith(newBar.cloneNode(true));

      reconcileState(state);
    });
  }

  function init() {
    var hosts = getHosts();
    log('init: found hosts', hosts.map(function (h) { return h.tagName; }));
    // .on() only exists once the theme has upgraded the host element.
    hosts.forEach(function (host) {
      customElements.whenDefined(host.tagName.toLowerCase()).then(function () {
        bindHost(host);
      });
    });
    reconcileState(stateFromRoot(document));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Fallback for cart mutations that bypass the per-component "update-nodes"
  // pub-sub entirely (e.g. other integrations calling /cart/update.js
  // directly and dispatching their own native document-level events once
  // their own DOM swap has completed).
  ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      getHosts().forEach(bindHost);
      reconcileState(stateFromRoot(document));
    });
  });
})();
